import { Component, effect, inject, untracked } from '@angular/core';
import { takeUntil } from 'rxjs';

import { DataService } from '../data.service';
import { Command, NavbarService } from '../navbar.service';
import { LatLng, LatLngBounds } from 'leaflet';
import { FilterService } from '../filter.service';
import { AuthService } from '../auth.service';
import { NeuerPunktFensterService } from '../bohrpunkte/neuer-punkt-fenster.service';
import { ImportWizardComponent } from '../import/import-wizard.component';
import { BatchEditWizardComponent } from '../bohrpunkte/batch-edit/batch-edit-wizard.component';
import { BatchEditHistoryComponent } from '../bohrpunkte/batch-edit/batch-edit-history.component';
import { ohnePersonenangaben, personenangabenVerbergen } from '../bohrpunkte/personenangaben';
import { Router } from '@angular/router';
import { AufnahmePunktDocument, bodenformLabelString, bodenformString, idOf, punktLabel, trackByIndex } from '../data-model';
import { formatBytes, LayerService, OfflineLayerService, OfflineDetailComponent, BaseMapListComponent, MigrationService, GeoExportService, GeoExportFormat, type GeoExportScope, FileExportService, buildPointsAndDrawingsLayers, buildGeomExportLayers, type GeomExportLayers, type GeomExportFeature, effectiveStatus, takeoverPreviousProject, takeoverHadInterimWork, WorkflowService, GEOM_LAYER_DEFAULTS, ServerBlockService, anmeldungFehlt,
	ConsoleErrorService,
} from '@luzula/luzula-lib';
import { datenbankMeldungZeigen, dbFehlerText, type DbMeldungsLage } from './db-meldung';
import { tiefenFuerExport } from './gis-export-tiefen';
import { GeomFeaturesService } from '../geom-features.service';
import { applyTakeoverMove, applyTakeoverRevert } from '../bohrpunkte/detail/point-takeover';
import { latLngBounds } from 'leaflet';
import { ProjectService } from '../project.service';
import { ZeichnungenService } from '../zeichnungen.service';
import { rehydrateDrawingFeatures } from '../import/project-backup-handler.service';
import { DeleteConfirmationService } from '../shared/delete-confirmation/delete-confirmation.service';
import { ProjectMarkersService } from '../project-markers.service';
import { firstValueFrom } from 'rxjs';

/** Waehlbarer Umfang des GIS-Exports (siehe Export-Dialog). */
type ExportScopeId = 'selection' | 'bounds' | 'all';

@Component({
    standalone: false,
    selector: 'bp-alle-bohrpunkte',
    templateUrl: './alle-bohrpunkte.component.html',
    styleUrls: ['./alle-bohrpunkte.component.css'],
})
export class AlleBohrpunkteComponent extends BaseMapListComponent<AufnahmePunktDocument> {
	private router = inject(Router);
	private ces = inject(ConsoleErrorService);
	public ds = inject(DataService);
	public fs = inject(FilterService);
	public ls = inject(LayerService);
	private ns = inject(NavbarService);
	public aus = inject(AuthService);
	private projectMarkers = inject(ProjectMarkersService);
	public ols = inject(OfflineLayerService);
	public ps = inject(ProjectService);
	private deleteConfirmation = inject(DeleteConfirmationService);
	private neuerPunktFenster = inject(NeuerPunktFensterService);
	private migrationService = inject(MigrationService);
	private geoExport = inject(GeoExportService);
	private fileExport = inject(FileExportService);
	private workflow = inject(WorkflowService);
	private zeichnungen = inject(ZeichnungenService);
	private geomFeatures = inject(GeomFeaturesService);
	// App-Vorgabe-Layer (Standort/Humus) — nur fuer id + Anzeigename im GIS-Export.
	private geomDefaults = inject(GEOM_LAYER_DEFAULTS);
	// Steht der Sitzungs-Balken schon? Dann schweigen die Kaesten (siehe db-meldung.ts).
	private serverBlock = inject(ServerBlockService);
	/** Der letzte Ladeversuch ist ausgefallen (kein Netz, keine Datenbank, Fehler). */
	private ladenAusstehend = false;
	/** Der zuletzt gesehene Netzzustand, fuer {@link beiNetzwechsel}. */
	private netzZuletzt = true;

	trackByIndex = trackByIndex;
	formatBytes = formatBytes;
	punktLabel = punktLabel;

	constructor() {
		super();
		this.navbarProvider.componentTitle = "Bohrpunkte";

		// Bei DB-Wechsel Punkte neu laden (falls nötig)
		let lastUserOfflineMode = this.oms.userOfflineMode();
		this.ds.currentDb_.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.ds.isInitialized) {
				const currentUserOfflineMode = this.oms.userOfflineMode();
				if (currentUserOfflineMode !== lastUserOfflineMode || !this.bps.length) {
					lastUserOfflineMode = currentUserOfflineMode;
					this.initPointsDebounced();
				}
			}
		});

		// Bei Offline-Modus-Wechsel Punkte neu laden
		this.oms.offlineModeChanged.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.initPointsDebounced();
		});

		// Netz wieder da: einen ausgefallenen Ladeversuch nachholen (siehe beiNetzwechsel)
		this.netzZuletzt = this.oms.networkOnline();
		effect(() => {
			const online = this.oms.networkOnline();
			untracked(() => this.beiNetzwechsel(online));
		});
	}

	// --- Abstract Method Implementations ---

	getPointLatLng(bp: AufnahmePunktDocument): LatLng | null {
		return this.ts.from(bp.header?.koordinaten);
	}

	matchesSearch(bp: AufnahmePunktDocument): boolean {
		if (!this.searchText || this.searchText.trim() === '') return true;

		const searchTerms = this.searchText.trim().split(/\s+/).filter(t => t.length > 0);
		if (searchTerms.length === 0) return true;

		// Case-insensitive Felder
		const label = this.punktLabel(bp, true)?.toLowerCase() || '';
		const nummer = bp.header?.nummer?.toLowerCase() || '';
		const forstamt = bp.header?.forstamt?.toLowerCase() || '';
		const revier = bp.header?.revier?.toLowerCase() || '';
		// Verborgene Personenangaben (Datennutzer, nicht angemeldet) sind auch nicht
		// suchbar — sonst verriete ein Treffer den Namen oder das Aufnahmejahr.
		const personenVerborgen = this.shouldMaskPersonalData();
		const kartierer = personenVerborgen ? '' : (bp.header?.aufnahme?.kartierer?.toLowerCase() || '');
		const type = bp.type?.toLowerCase() || '';
		const datumRaw = personenVerborgen ? '' : (bp.header?.aufnahme?.datum || '');
		const jahr = datumRaw ? new Date(datumRaw).getFullYear().toString() : '';
		const vegetation = bp.header?.vegetationsformengruppe?.toLowerCase() || '';

		// Case-sensitive Felder (FBF, HBF, HF)
		const bfArray = bp.header?.bodenform?.bf || [];
		const fbfValues = bfArray.map((b: any) => b.fbf || '').join(' ');
		const hbfValues = bfArray.map((b: any) => b.hbf || '').join(' ');
		const hf = bp.header?.humusform?.aufnahmeFlaeche?.neu || bp.header?.humusform?.aufnahmeFlaeche?.alt || '';

		return searchTerms.every(term => {
			const termLower = term.toLowerCase();
			const matchesInsensitive =
				label.includes(termLower) ||
				nummer.includes(termLower) ||
				forstamt.includes(termLower) ||
				revier.includes(termLower) ||
				kartierer.includes(termLower) ||
				type.includes(termLower) ||
				jahr.includes(termLower) ||
				vegetation.includes(termLower);
			const matchesSensitive =
				fbfValues.includes(term) ||
				hbfValues.includes(term) ||
				hf.includes(term);
			return matchesInsensitive || matchesSensitive;
		});
	}

	async initPoints(): Promise<void> {
		const currentVersion = ++this.initPointsVersion;
		try {
			this.splash.updateStatus('Auf Datenbank warten...');
			await this.ds.waitForInitialization();
			if (this.initPointsVersion !== currentVersion) return;
			this.splash.updateStatus('Punkte laden...');
			const result: any = await this.ds.queryIndex();
			if (this.initPointsVersion !== currentVersion) return;
			if (result === null) {
				// Keine DB verfügbar (Remote-DB fehlt/leer, kein Projekt): App TROTZDEM
				// starten — Splash ausblenden und den Nutzer klar informieren, statt
				// endlos im Splash zu haengen (initPoints kehrte hier bisher OHNE
				// splash.hide() zurueck → Dauer-Splash).
				console.log('initPoints: keine DB verfügbar, behalte bestehende Punkte');
				this.ladenAusstehend = true;
				// Nur melden, wenn die Meldung einen Adressaten hat: Solange die
				// Anmeldung nicht traegt, ist eine fehlende Datenbank keine Aussage
				// ueber die Datenbank — und beim Start waere sie schlicht falsch, die
				// Daten kommen eine Sekunde spaeter. Siehe db-meldung.ts.
				if (this.datenbankMeldungLohnt()) {
					this.msg.warning('Es konnte keine Datenbank geladen werden — die App startet ohne Daten.', 'Keine Datenbank!', { disableTimeOut: true, closeButton: true });
				}
				this.splash.hide();
				return;
			}
			const rows: any[] = result?.rows;
			if (!rows) { this.splash.hide(); return; }

			const points = new Array<LatLng>();
			const bps = new Array<any>();
			const pointsFilter = new Array<boolean>;

			rows.forEach((bp: any) => {
				const mesoklima = bp.key[7];
				const stfgData = bp.key[8];
				const extendedHeader: any = (mesoklima || stfgData) ? { mesoklima: mesoklima || undefined, stfg: stfgData ? { naehrkraftStufe: stfgData.nk, stammFeuchteStufe: { first: stfgData.sf1, second: stfgData.sf } } : undefined } : undefined;
				// Workflow-Block aus dem View-Key übernehmen (BohrpunktDocumentHandler emittiert
				// {status, createdBy} als 10. Element). Bestandsdaten ohne Block bleiben sichtbar.
				const workflow = bp.key[9] ?? undefined;
				let _bp: any = {_id: bp.id, type: bp.key[1], rotation: bp.key[10], header: bp.key[4], state: bp.key[5], version: bp.key[6], extendedHeader, workflow};
				// Die View liefert die Felder im SAVED-Format — wenn ein Doc seit dem
				// letzten Save in einer Migration umbenannt wurde (z.B. type-Korrekturen),
				// trägt der Index-Key noch die alte Version. Migration auch hier anwenden.
				try { _bp = this.migrationService.migrate(_bp); } catch { /* Doc bleibt unverändert */ }
				// Sichtbarkeitsfilter laut Workflow-Rolle: Kontrolleur sieht z.B. keine Bearbeitungs-Punkte,
				// Kartierer nur eigene Bearbeitungs-Punkte (siehe FilterService.applyWorkflowVisibility).
				if (!this.fs.applyWorkflowVisibility([_bp]).length) return;
				points.push(this.ts.from(_bp.header.koordinaten));
				bps.push(_bp);
				pointsFilter.push(this.fs.filteredDocIds.has(bp.id));
			});

			if (this.initPointsVersion !== currentVersion) return;
			this.bps = bps;
			this.invalidateMapMarkersCache();
			this.points = points;
			this.pointsFilter = pointsFilter;
			this.filteredCount = pointsFilter.filter(f => f).length;
			// Marker für andere Ansichten teilen (Druck-Layout-Snapshot spiegelt so die Punkte).
			this.projectMarkers.publish(this.mapMarkers, this.pointsFilter);
			this.invalidateSortCache();
			this.rebuildSortCache();
			this.updateBoundsFilter();
			this.ladenAusstehend = false;
			this.splash.hide();
		} catch (reason: any) {
			// Die technische Fassung bleibt in der Konsole — wer sucht, findet sie.
			console.error(reason);
			this.ladenAusstehend = true;
			if (this.datenbankMeldungLohnt()) {
				this.msg.error(`Eine Verbindung zur Datenbank konnte nicht hergestellt werden: ${dbFehlerText(reason)}`, 'Keine Verbindung', { disableTimeOut: true, closeButton: true });
			}
			this.splash.hide();
		}
	}

	/**
	 * Darf die Liste jetzt ueber die Datenbank klagen?
	 *
	 * Die Lage wird hier eingesammelt, entschieden wird sie in
	 * {@link datenbankMeldungZeigen} — dort steht auch, warum.
	 *
	 * `anmeldungFehlt` ist derselbe Blick, mit dem die Wache den Sitzungs-Balken
	 * aufstellt: EIN Urteil ueber die Anmeldung, nicht zwei, die auseinanderlaufen.
	 */
	private datenbankMeldungLohnt(): boolean {
		const lage: DbMeldungsLage = {
			balkenSteht: this.serverBlock.sessionExpired(),
			anmeldungFehlt: anmeldungFehlt(this.aus, {
				appOffline: this.oms.offlineMode(),
				geraetOnline: this.oms.networkOnline(),
			}),
			geraetOnline: this.oms.networkOnline(),
		};
		return datenbankMeldungZeigen(lage);
	}

	/**
	 * Das Netz gilt wieder als da: einen ausgefallenen Ladeversuch nachholen.
	 *
	 * Nutzer-Befund 04.10.2026 (bpweb 2.5.12, Desktop): Die App hielt das Netz
	 * eine Zeit lang fuer weg, die Liste fragte deshalb nicht beim Server - und
	 * lud danach nicht von selbst neu. Die Punkte kamen erst, als ein spaeteres
	 * Anmelde-Ereignis einen neuen Anlauf anstiess.
	 *
	 * Nur beim Wechsel von weg nach da und nur, wenn wirklich etwas aussteht:
	 * Waren die Punkte geladen, kostete jeder Netz-Wackler sonst eine Anfrage.
	 * Test: `punkte-nach-netzrueckkehr.spec.ts`.
	 */
	private beiNetzwechsel(online: boolean): void {
		const wiederDa = online && !this.netzZuletzt;
		this.netzZuletzt = online;
		if (wiederDa && this.ladenAusstehend) this.initPointsDebounced();
	}

	onPointDeleted(pointId: string): void {
		// Punkt sofort aus dem lokalen Array entfernen (optimistisch)
		const idx = this.bps.findIndex(bp => bp._id === pointId);
		if (idx !== -1) {
			this.bps.splice(idx, 1);
			this.bps = [...this.bps];
			if (this.points) {
				this.points.splice(idx, 1);
				this.points = [...this.points];
			}
			this.pointsFilter.splice(idx, 1);
			this.pointsFilter = [...this.pointsFilter];
			this.filteredCount = this.pointsFilter.filter(f => f).length;
			this.invalidateMapMarkersCache();
			this.invalidateSortCache();
			this.rebuildSortCache();
			this.updateBoundsFilter();
		}
	}

	initButtons(): void {
		this.ns.subscribe(Command.discard, () => this.onDiscard());
		if (this.ps.canI('checkin', 'points')) {
			this.ns.subscribe(Command.checkIn, () => this.onCheckin());
		} else {
			this.ns.unsubscribe(Command.checkIn);
		}

		if (this.ps.canI('checkout', 'points')) {
			this.ns.subscribe(Command.checkOut, () => this.onCheckout());
			this.ns.subscribe(Command.checkOutEmpty, () => this.onCheckoutEmpty());
		} else {
			this.ns.unsubscribe(Command.checkOut);
			this.ns.unsubscribe(Command.checkOutEmpty);
		}

		if (this.ps.canI('read', 'points')) {
			this.ns.subscribe(Command.gisExport, () => this.onGisExport());
			this.ns.subscribe(Command.exportPdf, () => this.onExportPdf());
		} else {
			this.ns.unsubscribe(Command.gisExport);
			this.ns.unsubscribe(Command.exportPdf);
		}

		// 3-stufige Subscription (User-Befund 2026-06-01): „kein Projekt geladen"
		// vor „keine Berechtigung" prüfen, damit die Meldung den Grund spricht.
		// Vorher subscribierten wir bedingt — `canIadd()` returnt false sowohl
		// bei „kein Projekt" als auch bei „kein Bearbeiter im Projekt", die
		// Meldung „keine Berechtigung" griff also auch im Fall „kein Projekt"
		// und war dort irreführend. (`canCreateNewPoint` im drawing-service
		// gatet den Modus-Start nur auf globale Auth-Rolle, daher kann der
		// User „Punkt setzen" auch ohne aktives Projekt anklicken.)
		this.ns.subscribe(Command.addPoint, (data?: any) => {
			if (!this.ps.project()) {
				this.msg.warning('Bitte zuerst ein Projekt öffnen.');
				return;
			}
			if (!this.ps.canIadd()) {
				this.msg.warning('Sie haben keine Berechtigung, Punkte in diesem Projekt anzulegen.');
				return;
			}
			this.onAddBohrpunkt(data);
		});

		if (this.ps.canIremove()) {
			this.ns.subscribe(Command.deleteSelection, () => this.deleteSelection());
		} else {
			this.ns.unsubscribe(Command.deleteSelection);
		}

		// Einfachmodell (PLAN-single-membership): Die generische Mehrfach-Zuordnung
		// (assignSelectionToProject/removeSelectionFromProject) entfällt — eine
		// Bohrung gehört zu genau EINEM Projekt. Übernahme läuft ausschließlich
		// über die Bulk-Freigabe unten (umhängen) bzw. den Per-Punkt-Button.

		// Bulk-Übernahme (P9/P11, G6): Manager (+ Entwickler-Bypass) holen
		// Altpunkte (Bestand/abgeschlossen) sammelweise in die Bearbeitung des
		// aktiven Projekts (Workflow-Restart + Umhängen). Pendant zum Per-Punkt-
		// Button.
		if (this.ps.canIfreigeben()) {
			this.ns.subscribe(Command.freigebenSelection, () => this.freigebenSelectionForEditing());
			// Umkehrung der Übernahme (User-Wunsch 2026-06-19): per Übernahme geholte
			// anstehende Punkte sammelweise zurücknehmen (→abgeschlossen, voriges
			// Projekt bzw. Bestand). Rechte IDENTISCH zur Übernahme (canIfreigeben).
			this.ns.subscribe(Command.zuruecknehmenSelection, () => this.zuruecknehmenSelection());
		} else {
			this.ns.unsubscribe(Command.freigebenSelection);
			this.ns.unsubscribe(Command.zuruecknehmenSelection);
		}

		// Die alten Kommandos `importDirectories`/`importFiles` (Excel-Direktimport
		// ohne Assistent) sind 2026-07-11 entfallen: sie legten Punkte ohne Projekt-
		// zuordnung und ohne Workflow an, die niemand bearbeiten konnte. Einziger
		// Import-Weg ist der Assistent. Die Enum-Eintraege bleiben unbenutzt stehen,
		// weil die Command-Nummern zwischen App und Lib gepaart sind (Umnummerieren
		// wuerde Lib-Abos auf fremde Kommandos schieben — siehe
		// navbar-command-conformance.spec.ts).
		if (this.ps.canIimport()) {
			this.ns.subscribe(Command.importWizard, () => this.onImportWizard());
		} else {
			this.ns.unsubscribe(Command.importWizard);
		}

		if (this.ps.canIbe('entwickler')) {
			this.ns.subscribe(Command.cleanUp, () => this.cleanUp());
		}

		// Batch-Editor: alle Projekt-Mitglieder; das Modal zeigt selbst an, wenn fuer die
		// aktuelle Rolle keine Aenderungen erlaubt sind. Der fruehere separate Menupunkt
		// "Status der Auswahl aendern" ist als dritter Pfad in den Batch-Edit-Assistenten
		// (Schritt 1) gewandert.
		// Phase I (I.6 / BE1): Batch-Edit-Assistent nur für schreibberechtigte
		// Mitglieder (Bearbeiter/Kontrolleur/Owner/Entwickler) — NICHT für Manager
		// (kein Punkt-Write) oder Datennutzer. Das Per-Punkt-Gate beim Execute (G8)
		// kommt zusätzlich.
		if (this.ps.canIbatchEdit()) {
			this.ns.subscribe(Command.batchEdit, () => this.onBatchEdit());
		} else {
			this.ns.unsubscribe(Command.batchEdit);
		}
		// Batch-Historie: alle Mitglieder + Manager (read-only Ansicht). Das
		// Undo-Eigentümer-Gate (G9) sitzt im Modal (canIundoBatch).
		if (this.ps.amIMember() || this.ps.canIbe('manager') || this.ps.canIbe('entwickler')) {
			this.ns.subscribe(Command.batchEditHistory, () => this.onBatchEditHistory());
		} else {
			this.ns.unsubscribe(Command.batchEditHistory);
		}
	}

	getSubscribedCommands(): number[] {
		return [
			Command.checkIn, Command.checkOut, Command.checkOutEmpty, Command.discard,
			Command.importWizard,
			Command.gisExport, Command.exportPdf,
			Command.addPoint, Command.deleteSelection,
			Command.freigebenSelection, Command.zuruecknehmenSelection,
			Command.cleanUp, Command.batchEdit, Command.batchEditHistory
		];
	}

	// --- Domain-Specific Methods ---

	private async onCheckout() {
		try {
			const pl = await this.storage.get('offlinePlusLevels');
			const st: any = await this.storage.get('MapStatus');
			const projectRegions = this.ls.regions()?.regions ?? [];
			const regionBounds = projectRegions.map(r => r.bounds);
			const regions = regionBounds.length > 0 ? regionBounds : (st?.bounds ? [new LatLngBounds(st.bounds._southWest, st.bounds._northEast)] : []);

			const modalRef = this.modalService.open(OfflineDetailComponent);
			modalRef.componentInstance.plusLevels = pl ? parseInt(pl) : 0;
			modalRef.componentInstance.regions = regions;

			const plusLevels: 0|1|2 = await firstValueFrom(modalRef.closed);
			await this.storage.set('offlinePlusLevels', plusLevels.toString());

			if (regions.length > 0) {
				this.ols.seedRequest.emit(plusLevels);
				const currentFilter = await this.storage.get('currentFilter');
				await this.ds.checkoutBohrpunkte(regions, currentFilter);
				this.fs.resetFilter();
				await this.oms.setOfflineMode(true);
				this.ls.offlineModeChanged.emit(true);
			}
		} catch (reason: any) {
			this.msg.error(reason.message);
		}
	}

	private async onCheckoutEmpty() {
		try {
			const pl = await this.storage.get('offlinePlusLevels');
			const st: any = await this.storage.get('MapStatus');
			const projectRegions = this.ls.regions()?.regions ?? [];
			const regionBounds = projectRegions.map(r => r.bounds);
			const regions = regionBounds.length > 0 ? regionBounds : (st?.bounds ? [new LatLngBounds(st.bounds._southWest, st.bounds._northEast)] : []);

			const modalRef = this.modalService.open(OfflineDetailComponent);
			modalRef.componentInstance.plusLevels = pl ? parseInt(pl) : 0;
			modalRef.componentInstance.regions = regions;

			const plusLevels: 0|1|2 = await firstValueFrom(modalRef.closed);
			await this.storage.set('offlinePlusLevels', plusLevels.toString());

			if (regions.length > 0) {
				this.ols.seedRequest.emit(plusLevels);
				await this.ds.checkoutEmpty();
				await this.oms.setOfflineMode(true);
				this.ls.offlineModeChanged.emit(true);
			}
		} catch (reason: any) {
			this.msg.error(reason.message);
		}
	}

	private async onCheckin() {
		const confirmed = await this.confirmService.confirm({
			title: 'Bohrpunkte synchronisieren',
			message: 'Sollen alle Bohrpunkte in die Online-Datenbank übernommen werden?\n\nSie bleiben im Offline-Modus und können weiterarbeiten.',
			confirmText: 'Synchronisieren',
			confirmClass: 'btn-success'
		});
		if (confirmed) {
			try {
				await this.ds.checkinDocuments();
			} catch (err) {
				// Ohne Server-Verbindung wird nichts uebertragen. Das muss man sehen —
				// frueher meldete die App hier stumm Erfolg (Befund 04.09.2026).
				console.error('[Einchecken] fehlgeschlagen:', err);
				this.msg.error(err instanceof Error ? err.message : 'Das Synchronisieren ist fehlgeschlagen.');
				return;
			}
			this.initPointsDebounced();
			this.msg.success('Bohrpunkte wurden synchronisiert');
		}
	}

	private async onDiscard() {
		const confirmed = await this.confirmService.confirmDiscard(
			'Sollen alle Änderungen an den offline verfügbar gemachten Bohrpunkten verworfen werden?'
		);
		if (confirmed) {
			await this.ds.discardLocal();
			await this.oms.setOfflineMode(false);
			this.ls.offlineModeChanged.emit(false);
			delete this.points;
		}
	}

	private onImportWizard() {
		const modalRef = this.modalService.open(ImportWizardComponent, { size: 'lg' });
		modalRef.closed.subscribe((result: { imported: number, skipped: number }) => {
			if (result.imported > 0) {
				this.initPoints();
			}
		});
	}

	private async onBatchEdit() {
		// Modal sofort oeffnen — die Komponente zeigt erstmal einen Spinner, waehrend
		// `queryIndex(true)` im Hintergrund laeuft. Bei grossen Datenbestaenden kann
		// das mehrere Sekunden dauern; ohne diese Vorab-Anzeige wirkt der Klick auf
		// den Menupunkt komplett unresponsive.
		const modalRef = this.modalService.open(BatchEditWizardComponent, { size: 'xl', scrollable: true });
		modalRef.closed.subscribe((report: { changedPoints?: number } | undefined) => {
			if (report && (report.changedPoints ?? 0) > 0) {
				this.initPoints();
			}
		});

		try {
			const response = await this.ds.queryIndex(true);
			if (!response) {
				modalRef.dismiss('no-data');
				return;
			}
			const allDocs = response.rows.map((r: any) => r.doc) as AufnahmePunktDocument[];
			if (allDocs.length === 0) {
				modalRef.dismiss('empty');
				this.msg.warning('Keine Punkte für Batch-Edit verfügbar');
				return;
			}

			const selectionIds = new Set<string>(this.selectionService.getSelection());
			const boundsAvailable = !!this.currentBounds;
			const boundsIds = new Set<string>();
			if (this.currentBounds) {
				for (const r of response.rows) {
					const ll = this.ts.from(r.doc.header?.koordinaten);
					if (ll && this.currentBounds.contains(ll)) boundsIds.add(r.doc._id);
				}
			}
			const filterIds = this.fs.currentFilterFunc ? new Set<string>(this.fs.filteredDocIds) : null;

			let initialSourceId: 'all' | 'selection' | 'bounds' | 'filter' = 'all';
			if (selectionIds.size > 0) initialSourceId = 'selection';
			else if (boundsAvailable) initialSourceId = 'bounds';
			else if (filterIds && filterIds.size > 0) initialSourceId = 'filter';

			const inst = modalRef.componentInstance;
			inst.allPoints = allDocs;
			inst.selectionIds = selectionIds;
			inst.boundsIds = boundsIds;
			inst.boundsAvailable = boundsAvailable;
			inst.filterIds = filterIds;
			inst.initialSourceId = initialSourceId;
			inst.start();
		} catch (err: any) {
			modalRef.dismiss('error');
			this.msg.error(err?.message || 'Fehler beim Laden der Punkte', 'Batch-Edit');
		}
	}

	private async onBatchEditHistory() {
		const response = await this.ds.queryIndex(true);
		if (!response) return;
		const docs = response.rows.map((r: any) => r.doc) as AufnahmePunktDocument[];
		const modalRef = this.modalService.open(BatchEditHistoryComponent, { size: 'xl' });
		modalRef.componentInstance.points = docs;
		modalRef.closed.subscribe((report: { changedPoints?: number } | undefined) => {
			if (report && (report.changedPoints ?? 0) > 0) {
				this.initPoints();
			}
		});
	}


	/**
	 * Der Umfang, den der Export-Dialog vorbelegt — dieselbe Rangfolge, nach der
	 * der Export frueher STILL entschieden hat: Auswahl vor Kartenausschnitt vor
	 * allem. Neu ist nur, dass der Nutzer sie im Dialog sieht und umstellen kann.
	 */
	private defaultExportScopeId(): ExportScopeId {
		if (this.selectionService.count() > 0) return 'selection';
		if (this.currentBounds) return 'bounds';
		return 'all';
	}

	/** true, wenn der Punkt vom aktiven Filter durchgelassen wird (ohne Filter: immer). */
	private passesFilter(id: string): boolean {
		return !this.fs.currentFilterFunc || this.fs.filteredDocIds.has(id);
	}

	/**
	 * Baut die waehlbaren Umfaenge fuer den Export-Dialog — mit Anzahl, damit vor
	 * dem Export sichtbar ist, was mitkommt. Die Zahlen stammen aus den bereits
	 * geladenen Punkten (`bps`), kosten also keine Datenbank-Abfrage.
	 *
	 * Ein aktiver Filter verengt „Kartenausschnitt" und „Alle" — wie bisher, denn
	 * die Punkte, die der Filter ausblendet, sieht der Nutzer gerade gar nicht.
	 * Der Dialog sagt das jetzt dazu (`hint`).
	 */
	private buildExportScopes(): { scopes: GeoExportScope[]; defaultScopeId: ExportScopeId; hint: string | null } {
		const filterActive = !!this.fs.currentFilterFunc;
		const inBounds = (bp: any) => {
			if (!this.currentBounds) return false;
			const ll = this.getPointLatLng(bp);
			return ll ? this.currentBounds.contains(ll) : false;
		};

		const visible = this.bps.filter((bp: any) => this.passesFilter(bp._id));
		const scopes: GeoExportScope[] = [];

		const selectionCount = this.selectionService.count();
		if (selectionCount > 0) {
			scopes.push({
				id: 'selection',
				label: 'Auswahl',
				count: selectionCount,
				hint: 'Zeichnungen und Flächen werden auf den Bereich der ausgewählten Punkte zugeschnitten.'
			});
		}
		if (this.currentBounds) {
			scopes.push({
				id: 'bounds',
				label: 'Kartenausschnitt',
				count: visible.filter(inBounds).length,
				hint: 'Zeichnungen und Flächen werden auf den Kartenausschnitt zugeschnitten.'
			});
		}
		scopes.push({
			id: 'all',
			label: filterActive ? 'Alle Filter-Treffer' : 'Alle Punkte',
			count: visible.length,
			hint: 'Zeichnungen und Flächen werden vollständig exportiert (kein Zuschnitt).'
		});

		const hint = filterActive
			? 'Filter aktiv: Kartenausschnitt und „Alle" enthalten nur Filter-Treffer.'
			: null;

		return { scopes, defaultScopeId: this.defaultExportScopeId(), hint };
	}

	private getExportPoints(allRows: any[], scopeId?: ExportScopeId | null): { points: any[], source: string } {
		const scope: ExportScopeId = scopeId ?? this.defaultExportScopeId();

		if (scope === 'selection' && this.selectionService.count() > 0) {
			const selectedIds = this.selectionService.getSelection();
			const points = allRows.filter((bp: any) => selectedIds.has(bp.doc._id));
			return { points, source: 'Auswahl' };
		}

		if (scope === 'bounds' && this.currentBounds) {
			const points = allRows.filter((bp: any) => {
				if (!this.passesFilter(bp.doc._id)) return false;
				const latLngPoint = this.ts.from(bp.doc.header?.koordinaten);
				return latLngPoint ? this.currentBounds!.contains(latLngPoint) : false;
			});
			return { points, source: 'Kartenausschnitt' };
		}

		const points = allRows.filter((bp: any) => this.passesFilter(bp.doc._id));
		return { points, source: this.fs.currentFilterFunc ? 'Filter-Treffer' : 'alle' };
	}

	/** Eine Regel fuer Export, Druck und Suche: `personenangaben.ts`. */
	private shouldMaskPersonalData(): boolean {
		return personenangabenVerbergen(this.aus);
	}

	private maskPointForExport(doc: any): any {
		return this.shouldMaskPersonalData() ? ohnePersonenangaben(doc) : doc;
	}

	private async onGisExport() {
		// Dialog ZUERST anzeigen — Umfang + Formate haengen an nichts, was erst
		// geladen werden muesste (die Anzahlen kommen aus den bereits geladenen
		// Punkten). Frueher erschien das Menue erst nach queryIndex(true) +
		// Maskierung + GeoJSON-Konvertierung; bei grossen Datenbestaenden wirkte
		// der Klick auf "GIS-Export" dadurch sekundenlang komplett unresponsive.
		const { scopes, defaultScopeId, hint } = this.buildExportScopes();
		const choice = await this.geoExport.pickExportOptions({
			defaults: ['geojson'],
			scopes,
			defaultScopeId,
			hint
		});
		if (!choice) return;
		const formats = choice.formats;
		const scopeId = (choice.scopeId ?? defaultScopeId) as ExportScopeId;

		// Speicherort SOFORT erfragen — noch im „frischen" Klick. Der Browser
		// oeffnet den Ordner-Dialog nur unmittelbar nach einer Nutzeraktion; nach
		// dem Laden + Umwandeln der Daten (Sekunden bis Minuten) wuerde er
		// wortlos uebersprungen und alles landete im Downloads-Ordner.
		// supported=false (z. B. Android) → weiter wie bisher (Teilen/Download).
		const target = await this.fileExport.pickDirectory();
		if (target.cancelled) return;

		const stopBusy = this.showExportBusyOverlay();
		let geoFiles: { filename: string; mimeType: string; content: any }[] | null = null;
		let summary = '';
		try {
			const response = await this.ds.queryIndex(true);
			if (!response) return;

			// Exportiert wird, was da ist: Punkte UND/ODER Zeichnungen. Kein
			// vorzeitiger Abbruch bei 0 Punkten — ein Projekt kann auch nur
			// Zeichnungen enthalten. Die „nichts zu exportieren"-Prüfung erfolgt
			// erst nach dem Bauen der Layer (siehe unten).
			const { points, source } = this.getExportPoints(response.rows, scopeId);

			// Dateinamen-Prefix: Projektname + Datum/Zeit
			const projectDoc = this.ps.currentProjectDoc();
			const projectName = (projectDoc?.name || 'Export').replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_');
			const now = new Date();
			const date = now.toISOString().slice(0, 10);
			const time = now.toTimeString().slice(0, 5).replace(':', '-');
			const prefix = `${projectName}_${date}_${time}`;

			const maskedPoints = points.map((bp: any) => this.maskPointForExport(bp.doc));

			// Punkte + Zeichnungen (Linien/Polygone) → GeoLayer[]. Optionaler bbox-Clip
			// auf Auswahl/Kartenausschnitt; generische Split-/Filter-Logik in der Lib
			// (buildPointsAndDrawingsLayers). LatLngBounds → GeoJSON-bbox [W,S,E,N].
			const zeichnungslayer = await this.loadDrawingLayersForExport();
			const bounds = this.getExportBounds(points, scopeId);
			const clipBbox = bounds
				? [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()] as [number, number, number, number]
				: null;

			const { layers, pointCount, lineCount, polygonCount } = buildPointsAndDrawingsLayers({
				points: this.pointsToGeoJson(maskedPoints) as any,
				pointsBaseFilename: `${prefix}_Punkte`,
				drawingLayers: zeichnungslayer,
				linesBaseFilename: `${prefix}_Zeichnungen_Linien`,
				polygonsBaseFilename: `${prefix}_Zeichnungen_Polygone`,
				clipBbox,
				// Je Zeichen-Ebene eine eigene Datei. Vorher landeten die Zeichnungen
				// ALLER Ebenen in einer gemeinsamen Linien- bzw. Flaechen-Datei, ohne
				// jeden Hinweis auf die Ebene — in QGIS war danach nicht mehr
				// erkennbar, was zusammengehoert (Nutzer-Befund 03.09.2026).
				proEbene: true
			});

			const zeichnungenCount = lineCount + polygonCount;

			// Editor-Flaechen (Standort/Humus/eigene Layer) an DENSELBEN Export
			// koppeln: ein GeoLayer je Thema, mit denselben Formaten/Dialog/Speicher-
			// weg wie Punkte + Zeichnungen. Gleicher bbox-Clip.
			const geomExport = await this.loadGeomExportLayers(prefix, clipBbox);
			const allLayers = [...layers, ...geomExport.layers];
			const flaechenCount = geomExport.featureCount;

			if (pointCount === 0 && zeichnungenCount === 0 && flaechenCount === 0) {
				this.msg.warning('Nichts zum Exportieren gefunden (keine Punkte, Zeichnungen oder Flächen)');
				return;
			}

			geoFiles = await this.geoExport.buildExportFilesForLayers(allLayers, formats);

			const formatLabel = formats.map((f: GeoExportFormat) => f.toUpperCase()).join(' + ');
			const parts: string[] = [];
			if (pointCount > 0) parts.push(`${pointCount} Punkte (${source})`);
			if (zeichnungenCount > 0) parts.push(`${zeichnungenCount} Zeichnungen`);
			if (flaechenCount > 0) parts.push(`${flaechenCount} Flächen`);
			summary = `GIS-Export: ${parts.join(', ')} als ${formatLabel}`;
		} catch (err: any) {
			this.msg.error(err?.message || 'Export fehlgeschlagen', 'Export-Fehler');
			return;
		} finally {
			// Spinner ausblenden, bevor saveMultipleFiles seine eigenen Dialoge
			// (Speichern/Teilen, Verzeichnis-Picker) oeffnet.
			stopBusy();
		}

		if (!geoFiles) return;
		try {
			const result = await this.fileExport.saveMultipleFiles(geoFiles, 'GIS-Export', {
				directoryHandle: target.handle
			});
			if (result.success) {
				this.msg.info(summary);
			}
		} catch (err: any) {
			this.msg.error(err?.message || 'Export fehlgeschlagen', 'Export-Fehler');
		}
	}

	/**
	 * Lädt die Zeichnungslayer für den GIS-Export inkl. Geometrien.
	 *
	 * Das Projekt-Doc (`zeichnungslayer`) trägt seit der DB-Umstellung nur noch das
	 * Layer-Skelett (Name/Stil/Sichtbarkeit) — die echten Features liegen im
	 * Zeichnungen-Store (`bpweb-zeichnungen`). Ohne Rehydrierung würde der Export
	 * Layer ganz ohne Linien/Polygone enthalten. Gleiches Muster wie der
	 * Projekt-Backup-Export (rehydrateDrawingFeatures). Bei Store-Fehlern (offline,
	 * o.ä.) Fallback auf das Doc-Skelett, damit der Export nicht abbricht.
	 */
	private async loadDrawingLayersForExport(): Promise<any[] | null> {
		const skeleton = await this.ps.getProjectValue("zeichnungslayer") as any[] | null;
		try {
			const pid = this.ps.project();
			await this.zeichnungen.openProject(pid);
			const storeLayers = await this.zeichnungen.listProject(pid);
			return (rehydrateDrawingFeatures(skeleton, storeLayers) ?? skeleton) as any[] | null;
		} catch (e) {
			// Der Nutzer bekommt eine Exportdatei, der man die Luecke nicht ansieht.
			console.warn('[GIS-Export] Zeichnungen aus Store laden fehlgeschlagen — Fallback auf Projekt-Doc:', e);
			this.ces.melde('[GIS-Export] Zeichnungen nur aus dem Projekt-Doc', e);
			return skeleton;
		}
	}

	/**
	 * Laedt die Editor-Flaechen (bpweb-geom: Standort/Humus/eigene Layer) und baut
	 * daraus die GeoLayer[] fuer den GIS-Export — dieselbe Pipeline wie Punkte/
	 * Zeichnungen (`buildGeomExportLayers` → `GeoExportService`). Bei Fehlern
	 * (offline, kein Projekt) werden die Flaechen ausgelassen, der Export bricht
	 * nicht ab.
	 */
	private async loadGeomExportLayers(
		prefix: string,
		clipBbox: [number, number, number, number] | null
	): Promise<GeomExportLayers> {
		const empty: GeomExportLayers = { layers: [], featureCount: 0, perLayer: [] };
		try {
			const pid = this.ps.project();
			if (!pid) return empty;
			await this.geomFeatures.openProject(pid);
			const docs = await this.geomFeatures.listProject(pid);
			if (!docs.length) return empty;
			const features = docs.map(d => ({ klasse: d.klasse, geometry: d.geometry, attribute: d.attribute })) as GeomExportFeature[];
			return buildGeomExportLayers({ features, catalog: await this.loadGeomCatalog(), baseFilenamePrefix: prefix, clipBbox });
		} catch (e) {
			// „werden ausgelassen" heisst: Die Datei ist unvollstaendig und sagt es nicht.
			console.warn('[GIS-Export] Editor-Flächen laden fehlgeschlagen — werden ausgelassen:', e);
			this.ces.melde('[GIS-Export] Editor-Flaechen fehlen im Export', e);
			return empty;
		}
	}

	/**
	 * Layer-Katalog fuer die Flaechen-Anzeigenamen: App-Vorgaben (Standort/Humus)
	 * + projekt-eigene Layer (aus `geomCustomLayers`). Nur id + Anzeigename — mehr
	 * braucht `buildGeomExportLayers` nicht.
	 */
	private async loadGeomCatalog(): Promise<{ id: string; displayName: string }[]> {
		const defaults = (this.geomDefaults ?? []).map(c => ({ id: c.id, displayName: c.displayName }));
		try {
			const raw = await this.ps.getProjectValue('geomCustomLayers') as { id?: string; name?: string }[] | null;
			const customs = (raw ?? [])
				.filter((r): r is { id: string; name: string } => !!r?.id && !!r?.name)
				.map(r => ({ id: r.id, displayName: r.name }));
			return [...defaults, ...customs];
		} catch {
			return defaults;
		}
	}

	/**
	 * Blendet einen modalen Spinner-Overlay ein, solange die (potenziell
	 * mehrsekuendige) Datenaufbereitung + Geo-Konvertierung laeuft.
	 * Rueckgabe: Dispose-Funktion, die den Overlay wieder entfernt (idempotent).
	 */
	private showExportBusyOverlay(): () => void {
		const overlay = document.createElement('div');
		overlay.setAttribute('aria-busy', 'true');
		overlay.style.cssText = `
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: rgba(0, 0, 0, 0.4);
			display: flex;
			align-items: center;
			justify-content: center;
			z-index: 9000;
		`;
		overlay.innerHTML = `
			<div style="
				background: white;
				border-radius: 8px;
				padding: 20px 28px;
				display: flex;
				align-items: center;
				gap: 14px;
				box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
				font-family: system-ui, -apple-system, sans-serif;
				color: #333;
				font-size: 14px;
			">
				<div class="spinner-border text-primary" role="status" style="width: 1.75rem; height: 1.75rem;">
					<span class="visually-hidden">Lädt…</span>
				</div>
				<span>Export wird vorbereitet…</span>
			</div>
		`;
		document.body.appendChild(overlay);
		let removed = false;
		return () => {
			if (removed) return;
			removed = true;
			overlay.remove();
		};
	}

	private onExportPdf() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.warning('Keine Punkte ausgewählt');
			return;
		}
		const ids = this.selectionService.getSelectionArray().join(',');
		this.router.navigate(['/bohrpunkte/Drucken/multi'], { queryParams: { ids } });
	}

	/**
	 * Der Bereich, auf den Zeichnungen und Flächen zugeschnitten werden — er
	 * folgt dem gewaehlten Umfang: Auswahl → Huellbereich der ausgewaehlten
	 * Punkte, Kartenausschnitt → aktueller Ausschnitt, Alle → kein Zuschnitt.
	 */
	private getExportBounds(points: any[], scopeId?: ExportScopeId | null): LatLngBounds | undefined {
		const scope: ExportScopeId = scopeId ?? this.defaultExportScopeId();

		if (scope === 'selection' && this.selectionService.count() > 0 && points.length > 0) {
			let bounds: LatLngBounds | undefined;
			points.forEach((bp: any) => {
				const latLngPoint = this.ts.from(bp.doc.header?.koordinaten);
				if (latLngPoint) {
					bounds = bounds ? bounds.extend(latLngPoint) : latLngBounds(latLngPoint, latLngPoint);
				}
			});
			return bounds;
		}
		if (scope === 'bounds') return this.currentBounds;
		return undefined;
	}

	private async onAddBohrpunkt(data?: any) {
		if (!this.ps.project()) {
			this.msg.warning('Bitte zuerst ein Projekt öffnen.');
			return;
		}
		// Hoechstens EIN Anlege-Fenster gleichzeitig: Kommt `addPoint` zweimal
		// (Doppeltipp, zwei Melder an der Karte), standen sonst zwei Fenster
		// uebereinander und es entstanden zwei Punkte an derselben Stelle.
		const modalRef = this.neuerPunktFenster.oeffne(fenster => fenster.position = data);
		if (!modalRef) return;
		modalRef.closed.subscribe((value: any) => {
			const bp = value.bp;
			const redirect = value.redirect;
			this.ds.createBohrpunktFrom(bp)?.then((response: any) => {
				if (redirect) {
					this.router.navigate(['/bohrpunkte', response.id]);
				} else {
					this.initPointsDebounced();
				}
			});
		})
	}

	printSelection() {
		const count = this.selectionService.count();
		if (count === 0) return;
		const ids = this.selectionService.getSelectionArray().join(',');
		this.router.navigate(['/bohrpunkte/Drucken/multi'], { queryParams: { ids } });
	}

	async deleteSelection() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.info('Keine Punkte ausgewählt');
			return;
		}

		// Sammle die ausgewählten Punkte für die erweiterte Bestätigung
		const selectedPoints = this.bps.filter(bp => this.selectionService.isSelected(bp._id));

		// Per-Punkt-Gate (Matrix §4.2 B8): nur eigene Punkte in `bearbeitung` (bzw.
		// Owner/Entwickler). Fremde/eingefrorene/in Kontrolle werden übersprungen,
		// nicht stillschweigend gelöscht.
		const deletable = selectedPoints.filter(bp => this.ps.canIRemovePoint(bp as any));
		const skipped = selectedPoints.length - deletable.length;
		if (deletable.length === 0) {
			this.msg.warning('Keine der ausgewählten Punkte darf gelöscht werden (nur eigene Punkte in Bearbeitung).');
			return;
		}

		const confirmed = await this.deleteConfirmation.confirmDeleteBulkFast(deletable);
		if (!confirmed) return;

		await this.ds.removeBohrpunkteBulk(deletable.map(bp => bp._id));
		this.selectionService.clear();
		this.msg.success(
			`${deletable.length} Punkte gelöscht` +
			(skipped > 0 ? `, ${skipped} übersprungen (keine Löschrechte)` : '')
		);
		this.initPoints();
	}

	/**
	 * Bulk-Freigabe (P9/P11, G6): holt die ausgewählten **Altpunkte**
	 * (Status `null`/Bestand oder `abgeschlossen`) per Workflow-Restart in die
	 * Bearbeitung des aktiven Projekts. Pendant zum Per-Punkt-Button „Zur
	 * Bearbeitung freigeben", deckt zusätzlich den eingefroren-fremd-
	 * `abgeschlossen`-Fall ab, den der Detail-Button überspringt (Plan §E).
	 *
	 * Bereits aktive Punkte (`bearbeitung`/`kontrolle`/`in_pruefung`/`abgelehnt`)
	 * werden NICHT zurückgesetzt (sonst würde fremde Arbeit überschrieben) —
	 * sie werden übersprungen und gemeldet. Reine Rollen-Capability sitzt im
	 * Menü-Gate (`canIfreigeben`), das Punkt-Status-Gate (Altpunkt) hier.
	 */
	async freigebenSelectionForEditing() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.info('Keine Punkte ausgewählt');
			return;
		}

		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) {
			this.msg.warning('Kein Projekt ausgewählt');
			return;
		}
		if (!this.ps.canIfreigeben()) {
			this.msg.warning('Nur Manager dürfen Punkte zur Bearbeitung freigeben');
			return;
		}

		const ids = this.selectionService.getSelectionArray();
		const points = await this.ds.bulkGet(ids);

		const releasable: any[] = [];
		let skipped = 0;
		for (const bp of points) {
			if (!bp?.header) continue;
			const status = effectiveStatus(bp);
			if (status === null || status === 'abgeschlossen') releasable.push(bp);
			else skipped++;
		}

		if (releasable.length === 0) {
			this.msg.warning(skipped > 0
				? `Nichts freigegeben: ${skipped} ausgewählte Punkt(e) sind bereits im Workflow (kein Altpunkt).`
				: 'Keine freigebbaren Punkte in der Auswahl');
			return;
		}

		const confirmed = await this.confirmService.confirm({
			title: 'In dieses Projekt übernehmen',
			message:
				`${releasable.length} Altpunkt(e) werden in „${projectDoc.name}" übernommen — ` +
				`der Workflow wird zurückgesetzt (die bisherige Historie bleibt als Reset-Marker erhalten); ` +
				`Punkte, die zu einem anderen Projekt gehören, werden dort entzogen.` +
				(skipped > 0 ? ` ${skipped} bereits aktive Punkt(e) der Auswahl werden übersprungen.` : '') +
				` Fortfahren?`,
			confirmText: 'Übernehmen',
			confirmClass: 'btn-success',
			cancelText: 'Abbrechen'
		});
		if (confirmed !== true) return;

		const user = this.aus.currentUser;
		const userId = this.aus.currentUserId ?? '';
		const toSave: any[] = [];
		// Einfachmodell: Übernahme hängt um (header.projects = [aktiv]); alte
		// Projekte verlieren den Punkt und müssen im Status nachgezogen werden.
		const leavingProjectIds = new Set<string>();
		for (const bp of releasable) {
			const leaving = applyTakeoverMove(bp, { id: projectDoc._id, name: projectDoc.name }, user, userId);
			for (const pid of leaving) leavingProjectIds.add(pid);
			toSave.push(bp);
		}

		await this.ds.bulkDocs(toSave);
		this.pps.invalidatePunkteCache();
		await this.pps.updateAssignedPoints();
		this.ls.assignedPointsChanged.emit(new Set(this.pps.assignedPointIds()));

		// Abgeleitete Projekt-Status nachziehen: verlassene Projekte + aktives.
		for (const pid of leavingProjectIds) {
			await this.workflow.refreshDerivedProjectStatus(pid);
		}
		await this.workflow.refreshDerivedProjectStatus(projectDoc._id);

		let resultMsg = `${toSave.length} Punkt${toSave.length > 1 ? 'e' : ''} in dieses Projekt übernommen`;
		if (skipped > 0) resultMsg += ` · ${skipped} übersprungen (bereits im Workflow)`;
		this.msg.success(resultMsg);
		this.selectionService.clear();
		this.initPoints();
	}

	/**
	 * Bulk-Rücknahme der Übernahme (Umkehrung von `freigebenSelectionForEditing`,
	 * User-Wunsch 2026-06-19): nimmt die ausgewählten, per Übernahme `anstehend`
	 * gewordenen Punkte zurück auf `abgeschlossen` und ordnet sie wieder ihrem
	 * **vorigen Projekt** zu (aus dem Reset-Marker). Existiert das Projekt nicht
	 * mehr, werden sie projektlos → Bestandspunkte.
	 *
	 * Eligibilität pro Punkt über `ProjectService.canIzuruecknehmenPoint` —
	 * identisch zum Per-Punkt-Knopf (Manager/Entwickler, echter Übernahme-
	 * Reset-Marker, im aktiven Projekt). Pool-Rückgaben (bearbeitung → anstehend)
	 * und alles andere werden übersprungen und gemeldet. Reine Rollen-Capability
	 * sitzt im Menü-Gate (`canIfreigeben`, identisch zur Übernahme).
	 */
	async zuruecknehmenSelection() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.info('Keine Punkte ausgewählt');
			return;
		}

		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) {
			this.msg.warning('Kein Projekt ausgewählt');
			return;
		}
		if (!this.ps.canIfreigeben()) {
			this.msg.warning('Nur Manager dürfen Übernahmen zurücknehmen');
			return;
		}

		const ids = this.selectionService.getSelectionArray();
		const points = await this.ds.bulkGet(ids);

		const revertable: any[] = [];
		let skipped = 0;
		for (const bp of points) {
			if (!bp?.header) continue;
			// Identisches Eligibilitäts-Gate wie der Einzelpunkt-Knopf.
			if (this.ps.canIzuruecknehmenPoint(bp)) revertable.push(bp);
			else skipped++;
		}

		if (revertable.length === 0) {
			this.msg.warning(skipped > 0
				? `Nichts zurückgenommen: ${skipped} ausgewählte Punkt(e) sind keine rücknehmbare Übernahme.`
				: 'Keine rücknehmbaren Übernahmen in der Auswahl');
			return;
		}

		// Warnung (Bestand-Ausnahme, User-Entscheidung 2026-06-19): Punkte, die NACH
		// der Übernahme noch gegriffen/bearbeitet wurden, gehen als „Abgeschlossen"
		// ins vorige Projekt zurück, ohne dort erneut kontrolliert zu werden.
		const interimCount = revertable.filter(bp => takeoverHadInterimWork(bp)).length;
		const confirmed = await this.confirmService.confirm({
			title: 'Übernahme zurücknehmen',
			message:
				`${revertable.length} übernommene(r) Punkt(e) werden zurückgenommen — zurück auf „Abgeschlossen" und ` +
				`wieder ihrem vorigen Projekt zugeordnet (existiert es nicht mehr, werden sie Bestandspunkte).` +
				(skipped > 0 ? ` ${skipped} Punkt(e) der Auswahl werden übersprungen (keine rücknehmbare Übernahme).` : '') +
				(interimCount > 0
					? ` ⚠ ${interimCount} dieser Punkt(e) wurden nach der Übernahme bearbeitet — etwaige Änderungen reisen als „Abgeschlossen" mit und werden im Zielprojekt NICHT erneut kontrolliert.`
					: '') +
				` Fortfahren?`,
			confirmText: 'Zurücknehmen',
			confirmClass: interimCount > 0 ? 'btn-danger' : 'btn-warning',
			cancelText: 'Abbrechen'
		});
		if (confirmed !== true) return;

		const user = this.aus.currentUser;
		const userId = this.aus.currentUserId ?? '';
		const toSave: any[] = [];
		const refreshProjectIds = new Set<string>();
		// Existenz-Cache für die vorigen Projekte (404 → null = Bestand) — ein
		// getOne pro Projekt-ID statt pro Punkt.
		const prevProjectCache = new Map<string, { id: string; name?: string } | null>();
		for (const bp of revertable) {
			const prev = takeoverPreviousProject(bp);
			let restoreTo: { id: string; name?: string } | null = null;
			if (prev?.id) {
				if (prevProjectCache.has(prev.id)) {
					restoreTo = prevProjectCache.get(prev.id)!;
				} else {
					try {
						const pdoc = await this.ps.getOne(prev.id) as { name?: string } | undefined;
						restoreTo = pdoc ? { id: prev.id, name: pdoc.name ?? prev.name } : null;
					} catch (_e) {
						restoreTo = null; // Projekt existiert nicht mehr → Bestand
					}
					prevProjectCache.set(prev.id, restoreTo);
				}
			}
			const leaving = applyTakeoverRevert(bp, restoreTo, user, userId);
			for (const pid of leaving) refreshProjectIds.add(pid);
			if (restoreTo) refreshProjectIds.add(restoreTo.id);
			toSave.push(bp);
		}

		await this.ds.bulkDocs(toSave);
		this.pps.invalidatePunkteCache();
		await this.pps.updateAssignedPoints();
		this.ls.assignedPointsChanged.emit(new Set(this.pps.assignedPointIds()));

		// Abgeleitete Projekt-Status nachziehen: vorige Ziele + aktives.
		for (const pid of refreshProjectIds) {
			await this.workflow.refreshDerivedProjectStatus(pid);
		}
		await this.workflow.refreshDerivedProjectStatus(projectDoc._id);

		let resultMsg = `${toSave.length} Übernahme${toSave.length > 1 ? 'n' : ''} zurückgenommen`;
		if (skipped > 0) resultMsg += ` · ${skipped} übersprungen (keine rücknehmbare Übernahme)`;
		this.msg.success(resultMsg);
		this.selectionService.clear();
		this.initPoints();
	}

	private pointsToGeoJson(bps: AufnahmePunktDocument[]) {
		const latLngToArray = (ll: LatLng) => [ll.lng, ll.lat];
		const hfOrVegFG = (bp: AufnahmePunktDocument) => {
			if (bp.header.humusform?.aufnahmeFlaeche) {
				if (bp.header.humusform.aufnahmeFlaeche.neu) return bp.header.humusform.aufnahmeFlaeche.neu;
				if (bp.header.humusform.aufnahmeFlaeche.alt) return bp.header.humusform.aufnahmeFlaeche.alt;
			}
			if (bp.header.vegetationsformengruppe) return bp.header.vegetationsformengruppe;
			return '';
		};
		return {
			type: 'FeatureCollection',
			features: bps.map((bp: AufnahmePunktDocument) => ({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: latLngToArray(this.ts.from(bp.header.koordinaten))
				},
				properties: {
					SYM_TEXT: bp.header.nummer,
					SYMBOL: idOf(bp.type),
					HF: hfOrVegFG(bp),
					HBF: bodenformString(bp.header.bodenform, 'hbf'),
					BF: bodenformLabelString(bp),
					Bemerkungen: bp.header.bemerkungen,
					// Kalktiefe/Spiegeltiefe fuer alle Punktarten, leer weggelassen
					// (Spaltentyp im GeoPackage, siehe gis-export-tiefen.ts).
					...tiefenFuerExport(bp)
				}
			}))
		};
	}
}
